import { Dialog } from '@material-ui/core';
import { BlueInfoIcon } from 'assets/icons';
import { AnimatedLock } from 'assets/icons/AnimatedLock';
import InfoIcon from 'assets/images/actions/infoIcon.svg?react';
import { DSButton, DSTag } from 'designSystem';
import { BetType } from 'features/coupon/component/CouponPanel/interface';
import { formatCouponPrice } from 'features/coupon/utility';
import { handleBalanceData } from 'features/customerPanel/store/cpPanel.action';
import { BonusInUseError } from 'features/offers/errors/BonusInUseError';
import { OfferStatus } from 'features/offers/interface/offers.interface';
import { waiveBonusAction } from 'features/offers/store/offers.action';
import { RewardModal } from 'features/RewardCard/components/RewardModal';
import { RewardProgressSection } from 'features/RewardCard/components/RewardProgressSection';
import { RewardCardProps } from 'features/RewardCard/RewardCard.types';
import { getUnlockedBets, getUnlockedSum } from 'features/RewardCard/utils/utils';
import { memo, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DialogCard from 'shared/components/dialog/dialogCard';
import Button from 'shared/components/form/button';
import { addNotification } from 'shared/components/toast';
import { BONUS_BALANCE } from 'shared/constants/common';
import { HumanReadableError } from 'shared/errors/HumanReadableError';
import { BalanceState, BETSType, State } from 'shared/interface/index';
import { addBet } from 'shared/store/coupon.action';
import { formatDate } from 'shared/util/utility';

export const RewardCard = memo(
	({ data, activateOffer, bonusesActive, type = BetType.Bonus, activateFreebet, handleBottomDrawer }: RewardCardProps) => {
		const balanceData = useSelector<State, BalanceState>((state) => state.cpPanel.balanceData);
		const { t } = useTranslation();
		const { nameKey, offerName, maxBonusAmount, bonusValue, unlockConditions } = data;
		const [openModal, setOpenModal] = useState<boolean>(false);
		const [dialogType, setDialogType] = useState('');
		const bets = useSelector<State, BETSType>((state) => state.coupon.bets);
		const dispatch = useDispatch();

		const unlockedSum = getUnlockedSum(unlockConditions);
		const unlockedBets = getUnlockedBets(unlockConditions);
		const transComponent = { span: <span className="font-semibold" /> };

		const flags = useMemo(
			() => ({
				isBonusActive: data.offerStatus === OfferStatus.ACTIVE,
				isBonusType: type === BetType.Bonus,
				isFreebetType: type === BetType.Freebet,
				isCurrentFreebetActivated: bets.freebetId === data.offerFreebetId,
				isBonusUnlocked: data.bonusUnlocked || !!data.eventAdditionalData?.adminId,
			}),
			[data.offerStatus, type, bets.freebetId, data.bonusUnlocked, data.unlockConditions, data.offerFreebetId],
		);

		const waiveOffer = async () => {
			if (bonusesActive && data.offerId) {
				try {
					setDialogType('');
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					await dispatch(waiveBonusAction(bonusesActive[0]!.offerId));
					activateOffer?.(data?.offerId);
				} catch (error) {
					if (error instanceof HumanReadableError) {
					} else if (error instanceof BonusInUseError) {
						addNotification('error', `${t('bonusInUseErrorMessage')}!`);
					} else {
						addNotification('error', `${t('genericErrorMessage')}!`);
					}
				}
			}
		};

		const bonusAmountLabel = flags.isBonusActive ? t('accessible_amount') : t('bonusAmount');
		const bonusContent = flags.isBonusActive ? (
			<Trans
				i18nKey={'rewardCardAvailableFunds'}
				values={{ availableSum: formatCouponPrice(data?.balanceValue?.amount ?? 0), totalSum: formatCouponPrice(maxBonusAmount ?? 0) }}
				components={transComponent}
			/>
		) : (
			<span className="font-semibold">
				{formatCouponPrice(bonusValue)} {t('currency')}
			</span>
		);

		const handleUseBonusClick = () => {
			dispatch(
				addBet({
					...bets,
					bonusBet: true,
					isFreeBetSelected: false,
					freebetId: null,
					freeBetStack: null,
				}),
			);
			dispatch(handleBalanceData({ ...balanceData, selectedBalanceType: BONUS_BALANCE }));
			handleBottomDrawer?.();
		};

		const handleBonusBalanceClick = () => {
			if (flags.isBonusActive) {
				setOpenModal(true);
				return;
			}

			if (bonusesActive?.length) {
				setDialogType('info');
				return;
			}

			activateOffer?.(data.offerId);
		};

		const handleFreebetClick = () => {
			if (data.freebetStake) {
				activateFreebet?.(data.offerId, data.freebetStake);
			}
		};

		const shouldHide = () => {
			return (
				(flags.isCurrentFreebetActivated && flags.isFreebetType) ||
				(flags.isBonusActive &&
					flags.isBonusUnlocked &&
					balanceData?.selectedBalanceType === BONUS_BALANCE &&
					!bets.freebetId &&
					!flags.isFreebetType)
			);
		};

		const renderBonusUse = () => (
			<DSButton size="small" color="tertiary" onClick={handleUseBonusClick}>
				{t('rewardCardUse')}
			</DSButton>
		);

		const renderBonusToggle = () => (
			<DSButton data-cy="betslip-bonus-activation" size="small" color="tertiary" onClick={handleBonusBalanceClick}>
				{flags.isBonusActive ? t('rewardCardUse') : t('activate')}
			</DSButton>
		);

		const renderFreebetUse = () => (
			<DSButton data-cy="betslip-freebet-activation" size="small" color="tertiary" onClick={handleFreebetClick}>
				{t('rewardCardUse')}
			</DSButton>
		);

		const actionButton = () => {
			if (shouldHide()) return null;
			if (flags.isBonusActive && flags.isBonusUnlocked && !flags.isFreebetType) return renderBonusUse();
			if (flags.isBonusType) return renderBonusToggle();
			if (flags.isFreebetType) return renderFreebetUse();
			return null;
		};

		return (
			<article className="border border-gray-200 dark:border-dark-400 bg-white dark:bg-dark-200 rounded-4 p-2">
				<div className="flex justify-between items-center mb-1">
					{flags.isBonusType ? (
						<>
							<DSTag className="uppercase" size="extra-small" color={data.offerStatus ? 'info' : 'neutral'}>
								{data.offerStatus ? t('rewardCardActive') : t('rewardCardInactive')}
							</DSTag>
							<DSTag className="uppercase" size="extra-small" color={flags.isBonusUnlocked ? 'success' : 'neutral'}>
								<div className="flex items-center gap-1">
									<AnimatedLock isComplete={flags.isBonusUnlocked} />
									{flags.isBonusUnlocked ? t('rewardCardUnlocked') : t('rewardCardLocked')}
								</div>
							</DSTag>
						</>
					) : (
						<>
							<DSTag className="uppercase" size="extra-small" color={bets.freebetId === data.offerId ? 'info' : 'neutral'}>
								{bets.freebetId === data.offerId ? t('rewardCardActive') : t('rewardCardAvailable')}
							</DSTag>
						</>
					)}
				</div>
				<div className="flex items-center justify-between">
					<div className="w-full">
						<div className="text-primary-500 font-bold dark:text-primary-100">{t(nameKey, offerName)}</div>

						<div className="flex items-center justify-start text-paragraph-xs">
							<span>
								{bonusAmountLabel}: {flags.isFreebetType ? `${data.freebetStake} ${t('currency')}` : bonusContent}
							</span>
						</div>
						{flags.isBonusActive && flags.isBonusUnlocked && (
							<div className="text-paragraph-xs mt-1">
								{t('rewardCardLeftToWager')} <span className="font-semibold">{`${data?.leftToWager} ${t('currency')}`}</span>
							</div>
						)}
						<div className="text-paragraph-xs mt-1">
							{t('validUntil')}: <span className="font-semibold">{formatDate(data.validTo, 'DD.MM.YYYY HH:mm')}</span>
						</div>
					</div>
					{flags.isBonusType && data.unlockConditions && (
						<InfoIcon
							className="cursor-pointer dark:text-primary-100 !stroke-current !dark:stroke-current"
							onClick={() => setOpenModal(!openModal)}
						/>
					)}
				</div>
				{flags.isBonusType && data.unlockConditions && (
					<RewardProgressSection
						isBonusActive={flags.isBonusActive}
						bonusId={data.offerId}
						unlockedBets={unlockedBets}
						unlockedSum={unlockedSum}
						unlockConditions={data.unlockConditions}
						isBonusUnlocked={flags.isBonusUnlocked}
					/>
				)}
				{actionButton()}
				{flags.isBonusType && (
					<RewardModal
						unlockConditions={data?.unlockConditions}
						setOpenModal={setOpenModal}
						openModal={openModal}
						unlockedBets={unlockedBets}
						unlockedSum={unlockedSum}
					/>
				)}
				{dialogType === 'info' && (
					<Dialog
						open={true}
						maxWidth="xs"
						fullWidth
						onClose={() => setDialogType('')}
						className="custom-dialog bg--transparent custom--box-shadow text--center"
						aria-labelledby="customized-dialog"
					>
						<DialogCard
							icon={BlueInfoIcon}
							className="info--card"
							subDescription={t('betslipInfoModalText')}
							cardBottomCustom={
								<div className="card__bottom card__bottom_multi-btn">
									<Button onClick={() => setDialogType('')} cyTestRef="no-button" btnType="dialog-info-cancel">
										{t('cancel')}
									</Button>
									<Button
										onClick={() => {
											waiveOffer();
										}}
										cyTestRef="yes-button"
										btnType="dialog-info-accept"
									>
										{t('activate')}
									</Button>
								</div>
							}
						/>
					</Dialog>
				)}
			</article>
		);
	},
);

RewardCard.displayName = 'RewardCard';
